import { AxiosResponse } from 'axios';
import React, {
  ReactNode,
  useState,
  createContext,
  useContext,
  useMemo,
  useEffect,
  Dispatch
} from 'react';
import { BasketAlertInterface } from '../shared/type/basketAlert.type';
import { CalculatePaymentInterfaceAssignedToConsumer } from '../shared/type/calculatePaymentResponse.type';
import {
  OrderForParentInterface,
  OrderForChildren,
  OrderedItemInterface
} from '../shared/type/orderForParent.type';
import { PlaceOrderForParentInterface } from '../shared/type/placeOrderForParent.type';
import calculateParentUpPayment from '../utils/calculators/calculateOrderValue';
import { useApi } from './ApiProvider';

interface Props {
  children: ReactNode;
}

interface UserDataInterface {
  consumerId: number;
  saldo: number;
  creditLimit: number;
  provision: string;
}

interface ParentBasketContextProps {
  parentBasketContext: OrderForParentInterface;
  isEmpty: boolean;
  basketAlerts: BasketAlertInterface[];
  addItemToBasket: (order: OrderForChildren) => void;
  deleteProductFromBasket: (consumerId: number, itemToRemove: OrderedItemInterface) => void;
  changeProductQuantity: (
    consumerId: number,
    itemToChange: OrderedItemInterface,
    increment: boolean,
    alreadyBoughtCount: number
  ) => void;
  resetBasket: () => void;
  setUserData: Dispatch<React.SetStateAction<UserDataInterface[]>>;
  calculatedPayment: CalculatePaymentInterfaceAssignedToConsumer | undefined;
  placeOrderForPayment: () => Promise<void>;
  orderSummary: PlaceOrderForParentInterface | undefined;
  handleUseCreditToggle: (consumerId: number, checked: boolean) => void;
  scrollToItemKey: string | null;
  setScrollToItemKey: Dispatch<React.SetStateAction<string | null>>;
}

const defaultBasket: OrderForParentInterface = {
  ordersForChildren: []
};

const ParentDashboardContextContext = createContext<ParentBasketContextProps | undefined>(
  undefined
);

export const useParentBasketContext = () => {
  const context = useContext(ParentDashboardContextContext);
  if (!context) {
    throw new Error('useParentBasketContext must be used within a ParentBasketContextProvider');
  }
  return context;
};

export const ParentBasketContextProvider = ({ children }: Props) => {
  const { apiOrderController } = useApi();
  const [parentBasketContext, setParentBasketContext] =
    useState<OrderForParentInterface>(defaultBasket);
  const [calculatedPayment, setCalculatedPayment] = useState<
    CalculatePaymentInterfaceAssignedToConsumer | undefined
  >(undefined);
  const [userData, setUserData] = useState<UserDataInterface[]>([]);
  const [orderSummary, setOrderSummary] = useState<PlaceOrderForParentInterface>();
  const [useCreditByConsumer, setUseCreditByConsumer] = useState<{ [consumerId: number]: boolean }>(
    {}
  );
  const [scrollToItemKey, setScrollToItemKey] = useState<string | null>(null);
  const [basketAlerts, setBasketAlerts] = useState<BasketAlertInterface[]>([]);
  const [isEmpty, setIsEmpty] = useState<boolean>(true);

  const handleUseCreditToggle = (consumerId: number, checked: boolean) => {
    setUseCreditByConsumer((prevState) => ({
      ...prevState,
      [consumerId]: checked
    }));

    setParentBasketContext((prevContext) => ({
      ...prevContext,
      ordersForChildren: prevContext.ordersForChildren.map((order) =>
        order.consumerId === consumerId ? { ...order, useCredit: checked } : order
      )
    }));
  };

  function addItemToBasket(newItem: OrderForChildren) {
    setParentBasketContext((prevContext) => {
      const existingOrder = prevContext.ordersForChildren.find(
        (order) => order.consumerId === newItem.consumerId
      );

      let updatedOrdersForChildren = [...prevContext.ordersForChildren];

      // Jeśli zamówienie dla ucznia już istnieje
      if (existingOrder) {
        const updatedOrderedItems = [...existingOrder.orderedItems];

        newItem.orderedItems.forEach((newOrderedItem) => {
          const existingItem = updatedOrderedItems.find(
            (item) =>
              item.when === newOrderedItem.when &&
              item.purchasableItem.name === newOrderedItem.purchasableItem.name &&
              item.purchasableItem.price === newOrderedItem.purchasableItem.price
          );

          const refKey = `${newItem.consumerId}-${newOrderedItem.when}-${newOrderedItem.purchasableItem.name}`;
          setScrollToItemKey(refKey);

          if (existingItem) {
            // Jeśli produkt już istnieje → zwiększamy jego ilość
            changeProductQuantity(newItem.consumerId, existingItem, true, existingItem.count);
          } else {
            // Jeśli produkt nie istnieje → sprawdź limity przed dodaniem
            const inBasketCount = updatedOrderedItems
              .filter(
                (item) =>
                  item.when === newOrderedItem.when &&
                  item.purchasableItem.name === newOrderedItem.purchasableItem.name &&
                  item.purchasableItem.price === newOrderedItem.purchasableItem.price
              )
              .reduce((sum, item) => sum + item.count, 0);

            if (
              !checkForAlerts(
                newItem.consumerId,
                true,
                newOrderedItem,
                newOrderedItem.purchasableItem.alreadyBoughtCount
              )
            ) {
              updatedOrderedItems.push(newOrderedItem);
            }
          }
        });

        updatedOrdersForChildren = updatedOrdersForChildren.map((order) =>
          order.consumerId === existingOrder.consumerId
            ? { ...order, orderedItems: updatedOrderedItems }
            : order
        );
      } else {
        // Zamówienie dla ucznia jeszcze nie istnieje
        const firstItem = newItem.orderedItems[0];
        const refKey = `${newItem.consumerId}-${firstItem.when}-${firstItem.purchasableItem.name}`;
        setScrollToItemKey(refKey);

        // sprawdź limity dla KAŻDEGO nowego produktu
        const limitExceeded = newItem.orderedItems.some((item) =>
          checkForAlerts(newItem.consumerId, true, item, item.purchasableItem.alreadyBoughtCount)
        );

        if (!limitExceeded) {
          updatedOrdersForChildren.push(newItem);
        }
      }

      return {
        ...prevContext,
        ordersForChildren: updatedOrdersForChildren
      };
    });
  }

  async function calculatePayment() {
    try {
      const consumerCalculationRequests = parentBasketContext.ordersForChildren
        .map((children) => {
          const foundConsumer = userData.find(
            (consumer) => consumer.consumerId === children.consumerId
          );

          if (foundConsumer) {
            const { saldo, creditLimit, provision } = foundConsumer;

            const useCredit = useCreditByConsumer[children.consumerId] ?? false;

            return {
              consumerId: children.consumerId,
              orderCalculationRequestDto: {
                orderedItems: children.orderedItems,
                initialSaldo: saldo ?? 0,
                credit: creditLimit,
                provision: provision,
                useCredit
              }
            };
          } else {
            console.error(`No consumer found with id ${children.consumerId}`);
            return null;
          }
        })
        .filter((request) => request !== null);

      if (consumerCalculationRequests.length > 0) {
        // const response: AxiosResponse<CalculatePaymentInterfaceAssignedToConsumer> = await apiOrderController('calculate-up-payment').post('', {
        //   consumerCalculationRequests: consumerCalculationRequests
        // });
        const response: CalculatePaymentInterfaceAssignedToConsumer = calculateParentUpPayment({
          consumerCalculationRequests
        });

        // const result = calculateParentUpPayment({ consumerCalculationRequests });
        //
        // console.log(result);

        setCalculatedPayment(response);
      } else {
        console.error('No valid consumer data to process');
      }
    } catch (error) {
      console.error('Error calculating payment:', error);
    }
  }

  const placeOrderForPayment = async () => {
    try {
      const ordersForChildren = parentBasketContext.ordersForChildren.map((order) => ({
        tenantId: order.tenantId,
        consumerId: order.consumerId,
        useCredit: order.useCredit,
        orderedItems: order.orderedItems.map((item) => ({
          purchasableItem: {
            name: item.purchasableItem.name,
            meals: item.purchasableItem.meals.map((meal) => ({
              mealToDayId: meal.mealToDayId,
              courseName: meal.courseName,
              mealDto: {
                id: meal.mealDto?.id,
                name: meal.mealDto?.name,
                description: meal.mealDto?.description,
                allergens: meal.mealDto?.allergens.map((allergen) => ({
                  id: allergen.id,
                  formalNumber: allergen.formalNumber,
                  name: allergen.name
                }))
              }
            })),
            price: item.purchasableItem.price,
            originalPrice: item.purchasableItem.originalPrice,
            stakeId: item.purchasableItem.stakeId || 0,
            alreadyBoughtCount: item.purchasableItem.alreadyBoughtCount || 0,
            orderCount: item.purchasableItem.orderCount || 0,
            stakeLimitLeft: item.purchasableItem.stakeLimitLeft ?? null,
            itemPurchaseLimit: item.purchasableItem.itemPurchaseLimit ?? null
          },
          count: item.count,
          when: item.when
        }))
      }));

      const response: AxiosResponse<PlaceOrderForParentInterface> = await apiOrderController(
        'place-order-for-parent'
      ).post('', {
        ordersForChildren: ordersForChildren.filter((kid) => kid.orderedItems.length > 0)
      });

      setOrderSummary(response.data);
    } catch (error) {
      console.error('Error placing order:', error);
    }
  };

  function deleteProductFromBasket(consumerId: number, itemToRemove: OrderedItemInterface) {
    setParentBasketContext((prevContext) => ({
      ...prevContext,
      ordersForChildren: prevContext.ordersForChildren
        .map((order) => {
          if (order.consumerId !== consumerId) return order;

          const filteredItems = order.orderedItems.filter((item) => {
            if (
              item.purchasableItem.name !== itemToRemove.purchasableItem.name ||
              item.purchasableItem.price !== itemToRemove.purchasableItem.price
            ) {
              return true;
            }

            if (itemToRemove.connectedDates?.length) {
              return !itemToRemove.connectedDates.includes(item.when);
            }

            return item.when !== itemToRemove.when;
          });

          if (filteredItems.length === 0) {
            return null;
          }

          return {
            ...order,
            orderedItems: filteredItems
          };
        })
        .filter((order): order is typeof order & {} => order !== null)
    }));
  }

  function changeProductQuantity(
    consumerId: number,
    itemToChange: OrderedItemInterface,
    increment: boolean,
    alreadyBoughtCount: number
  ) {
    if (
      checkForAlerts(
        consumerId,
        increment,
        itemToChange,
        itemToChange.purchasableItem.alreadyBoughtCount
      )
    ) {
      return;
    }

    setParentBasketContext((prevContext) => ({
      ...prevContext,
      ordersForChildren: prevContext.ordersForChildren.map((order) => {
        if (order.consumerId !== consumerId) return order;

        return {
          ...order,
          orderedItems: order.orderedItems.map((item) => {
            const isSameItem =
              item.purchasableItem.name === itemToChange.purchasableItem.name &&
              item.purchasableItem.price === itemToChange.purchasableItem.price;

            const isSameDate = item.when === itemToChange.when;

            const isConnectedDate = itemToChange.connectedDates?.includes(item.when);

            //changed quantity when enforced for date range is set on
            if (itemToChange.connectedDates && itemToChange.connectedDates?.length > 0) {
              if (isSameItem && isConnectedDate) {
                const newCount = increment ? item.count + 1 : Math.max(item.count - 1, 1);
                return { ...item, count: newCount };
              }
            } else {
              if (isSameItem && isSameDate) {
                const newCount = increment ? item.count + 1 : Math.max(item.count - 1, 1);
                return { ...item, count: newCount };
              }
            }

            return item;
          })
        };
      })
    }));
  }

  function checkForAlerts(
    consumerId: number,
    isIncrement: boolean,
    item: OrderedItemInterface,
    alreadyBoughtCount: number
  ) {
    const { itemPurchaseLimit, stakeLimitLeft, stakeId } = item.purchasableItem;

    if (!isIncrement) return false;

    // 🛑 Grupa: limit stake
    if (stakeLimitLeft !== null && stakeLimitLeft <= 0) {
      addAlert(
        'Przekroczono grupowy limit zakupowy. Pozycja nie może zostać dodana do koszyka.',
        consumerId.toString(),
        stakeId,
        item.when
      );
      return true;
    }

    // ✅ Liczymy ile już jest w koszyku tego itemu
    const currentCount =
      parentBasketContext.ordersForChildren
        .find((order) => order.consumerId === consumerId)
        ?.orderedItems.filter(
          (orderItem) =>
            orderItem.when === item.when &&
            orderItem.purchasableItem.name === item.purchasableItem.name &&
            orderItem.purchasableItem.price === item.purchasableItem.price
        )
        .reduce((sum, orderItem) => sum + orderItem.count, 0) ?? 0;

    // 🔥 WAŻNE: Dodajemy alreadyBoughtCount
    const totalCount = currentCount + alreadyBoughtCount;

    // Symulujemy jak będzie po dodaniu 1 sztuki
    const simulatedCount = totalCount + 1;

    if (itemPurchaseLimit !== null && simulatedCount > itemPurchaseLimit) {
      addAlert(
        'Osobisty limit zakupowy osiągnięty na dany dzień, nie można dodać więcej tego produktu.',
        consumerId.toString(),
        stakeId,
        item.when
      );
      return true;
    }

    return false;
  }

  const removeAlert = (consumerId: string, stakeId: number, when: string) => {
    setBasketAlerts((prevAlerts) =>
      prevAlerts.map((alert) =>
        alert.consumerId === consumerId && alert.stakeId === stakeId && alert.when === when
          ? { ...alert, visible: false }
          : alert
      )
    );

    setTimeout(() => {
      setBasketAlerts((prevAlerts) =>
        prevAlerts.filter(
          (alert) =>
            alert.consumerId !== consumerId || alert.stakeId !== stakeId || alert.when !== when
        )
      );
    }, 500);
  };

  const addAlert = (text: string, consumerId: string, stakeId: number, when: string) => {
    setBasketAlerts((prevAlerts) => {
      if (
        prevAlerts.some(
          (alert) =>
            alert.consumerId === consumerId &&
            alert.stakeId === stakeId &&
            alert.text === text &&
            alert.when === when
        )
      ) {
        return prevAlerts;
      }
      const newAlert = { text, consumerId, stakeId, when, timeout: 10000, visible: true };
      return [...prevAlerts, newAlert];
    });

    setTimeout(() => {
      removeAlert(consumerId, stakeId, when);
    }, 10000);
  };

  function resetBasket() {
    setParentBasketContext(defaultBasket);
  }

  useEffect(() => {
    if (parentBasketContext.ordersForChildren.length > 0) {
      setIsEmpty(false);
    } else {
      setIsEmpty(true);
    }
    calculatePayment();
  }, [parentBasketContext]);

  const contextValue = useMemo(
    () => ({
      parentBasketContext,
      isEmpty,
      addItemToBasket,
      resetBasket,
      basketAlerts,
      deleteProductFromBasket,
      changeProductQuantity,
      setUserData,
      calculatedPayment,
      placeOrderForPayment,
      orderSummary,
      handleUseCreditToggle,
      scrollToItemKey,
      setScrollToItemKey
    }),
    [
      parentBasketContext,
      isEmpty,
      addItemToBasket,
      resetBasket,
      basketAlerts,
      deleteProductFromBasket,
      changeProductQuantity,
      setUserData,
      calculatedPayment,
      placeOrderForPayment,
      orderSummary,
      handleUseCreditToggle,
      scrollToItemKey,
      setScrollToItemKey
    ]
  );

  return (
    <ParentDashboardContextContext.Provider value={contextValue}>
      {children}
    </ParentDashboardContextContext.Provider>
  );
};
